import React from 'react';
import Link from 'next/link';
import type { Locale } from './config';

const B = ({ children }: { children: React.ReactNode }) => (
  <span className="text-foreground font-semibold">{children}</span>
);

const en = {
  meta: {
    title: 'TCGSecret — Trading Card Game Shop',
    description: 'Online TCG shop. Booster boxes, ETBs, packs, bundles and more for Pokémon, One Piece, Lorcana, Dragon Ball and more.',
  },
  nav: {
    home: 'Home',
    about: 'About',
    aboutUs: 'About Us',
    contact: 'Contact',
    categories: 'Categories',
    allCategories: 'All Categories',
    allTcgs: 'All TCGs',
    search: 'Search',
    preorders: 'Pre-orders',
    store: 'Store',
  },
  common: {
    soldOut: 'Sold Out',
    loading: 'Loading...',
    loadingProduct: 'Loading product...',
    productCount: (n: number) => `${n} product${n === 1 ? '' : 's'}`,
    noProducts: 'No products found',
    browseAllTcgs: 'Browse all TCGs',
  },
  header: {
    searchAria: 'Search products',
    lightMode: 'Switch to light mode',
    darkMode: 'Switch to dark mode',
    openMenu: 'Open menu',
    closeMenu: 'Close menu',
    language: 'Language',
    searchTitle: 'Search Products',
    closeSearch: 'Close search',
    searchPlaceholder: 'Search by name, TCG, set, or type…',
    searchButton: 'Search',
    searchHint: <>Type a search term and press <B>Search</B> or hit <B>Enter</B></>,
    noResultsHint: 'Try a different search term or TCG filter.',
  },
  results: (count: number, query?: string, game?: string) => (
    <>
      Found <B>{count}</B> result{count !== 1 ? 's' : ''}
      {query && <> for &ldquo;<B>{query}</B>&rdquo;</>}
      {game && <> in <B>{game}</B></>}
    </>
  ),
  hero: {
    eyebrow: 'Trading Card Game Shop',
    tagline: 'Your vault for everything trading cards. Booster boxes, elite trainer boxes, packs, bundles & more — across all your favourite TCGs.',
    browseAll: 'Browse All TCGs',
    aboutShop: 'About the Shop',
    scroll: 'Scroll',
  },
  bento: {
    eyebrow: 'TCG Vault',
    title: 'Browse by TCG',
    subtitle: 'More TCGs added regularly.',
  },
  featured: {
    eyebrow: 'Current Inventory',
    title: 'Featured Products',
    restocking: 'Restocking soon',
    viewAll: 'View all products',
  },
  about: {
    eyebrow: 'About TCGSecret',
    title: 'Your trusted vault for all things TCG',
    p1: 'TCGSecret is an online shop for trading card game fans and collectors. We love the thrill of the hobby — from cracking a fresh booster box to finding that one card that completes your collection.',
    p2: 'We stock a wide range of official products across all major TCG titles — from the iconic Pokémon TCG to the newest arrivals like Riftbound and Cyberpunk 2077 TCG.',
    browseProducts: 'Browse Products',
  },
  contact: {
    eyebrow: 'Contact',
    title: 'Get in touch',
    body: "Questions about a product, restocks or an order? Send us an email and we'll get back to you as soon as possible.",
  },
  footer: {
    tagline: 'Your trusted vault for all things TCG.',
    about: 'About',
    contact: 'Contact',
    email: 'Email',
    phone: 'Phone',
    terms: 'Terms',
    privacy: 'Privacy',
    returns: 'Returns',
    shipping: 'Shipping',
    storeSoon: 'Store near The Hague (coming soon)',
  },
  category: {
    allProducts: 'All Products',
    allSoldOut: 'All items currently sold out',
    showing: (count: number, category?: string) => (
      <>
        Showing <B>{count}</B> products
        {category && <> in <B>{category}</B></>}
      </>
    ),
    checkBack: 'Check back soon as we restock our inventory.',
    exploreOther: 'Explore Other TCGs',
  },
  product: {
    notFoundTitle: 'Product Not Found',
    notFoundBody: "The product you're looking for doesn't exist or has been removed.",
    browseAllProducts: 'Browse All Products',
    restockTitle: 'Restock in progress.',
    restockBody: "We're sourcing new stock — check back soon or follow us for updates.",
    description: 'Description',
    details: 'Product Details',
    tcg: 'TCG',
    set: 'Set',
    type: 'Type',
    condition: 'Condition',
    conditionValue: 'Sealed / New',
    region: 'Region',
    regionValue: 'English',
    availability: 'Availability',
    outOfStock: 'Out of Stock',
    moreFrom: (game: string) => `More ${game}`,
    moreLikeThis: 'More Like This',
    related: 'Related Products',
    viewAll: 'View all →',
  },
  search: {
    metaTitle: 'Search Products — TCGSecret',
    metaDescription: 'Search TCG products at TCGSecret.',
    clear: 'Clear search',
    showingAll: (count: number) => <>Showing all <B>{count}</B> products</>,
    noResultsHint: 'Try a different search term or browse by TCG category.',
    clearFilters: 'Clear filters',
  },
  cookies: {
    title: 'We value your privacy',
    body: 'We use cookies to enhance your browsing experience. You can choose which cookies you allow. Necessary cookies are always active as they are required for the website to function.',
    privacyPolicy: 'Privacy Policy',
    necessary: 'Necessary',
    alwaysActive: '(always active)',
    necessaryDesc: 'Required for the website to function. Cannot be disabled.',
    functional: 'Functional',
    functionalDesc: 'Remember your preferences such as language and region settings.',
    toggle: { functional: 'Toggle functional cookies' },
    acceptAll: 'Accept All',
    rejectAll: 'Reject All',
    save: 'Save Preferences',
    manage: 'Manage Preferences',
  },
  notFound: {
    title: 'Page Not Found',
    body: "The page you're looking for doesn't exist. Let's get you back!",
    back: 'Go Back',
    home: 'Back to Home',
  },
  legal: {
    lastUpdated: 'Last updated',
  },
  shop: {
    addToCart: 'Add to cart',
    preorder: 'Pre-order',
    inStock: 'In stock',
    quantity: 'Quantity',
    releases: (date: string) => `Releases ${date}`,
    releaseDate: 'Release date',
    preorderTitle: 'Pre-order now.',
    preorderBody: "We ship as soon as the stock arrives. You can cancel free of charge until it ships.",
  },
  cart: {
    title: 'Your cart',
    open: (n: number) => `Open cart (${n} item${n === 1 ? '' : 's'})`,
    close: 'Close cart',
    empty: 'Your cart is empty',
    emptyHint: 'Add a booster box, or pre-order the next big release.',
    subtotal: 'Subtotal',
    shippingAtCheckout: 'Shipping is calculated at checkout.',
    checkout: 'Checkout',
    continueShopping: 'Continue shopping',
    remove: 'Remove',
    increase: 'Increase quantity',
    decrease: 'Decrease quantity',
    preorderNote: 'Your cart contains a pre-order. We ship everything together once the pre-order product is in.',
  },
  checkout: {
    metaTitle: 'Checkout — TCGSecret',
    eyebrow: 'Almost there',
    title: 'Checkout',
    summary: 'Order summary',
    shipTo: 'Ship to',
    shipping: 'Shipping',
    free: 'Free',
    freeFrom: (amount: string) => `Free shipping from ${amount}`,
    delivery: (days: string) => `Delivered in ${days} business days`,
    total: 'Total',
    vatIncluded: 'Incl. VAT',
    terms: (
      <>
        I agree to the <Link href="/terms" target="_blank" className="text-primary hover:underline">Terms &amp; Conditions</Link> and have read the{' '}
        <Link href="/privacy-policy" target="_blank" className="text-primary hover:underline">Privacy Policy</Link>.
      </>
    ),
    pay: 'Order with obligation to pay',
    redirecting: 'Redirecting to payment…',
    secure: 'You pay securely on the next page via our payment provider Stripe. Your address is entered there too.',
    errorGeneric: 'Something went wrong while starting your payment. Please try again.',
    errorUnavailable: 'One or more products in your cart are no longer available. Please review your cart.',
    errorNotConfigured: 'Online payment is not available yet. Please email us to place your order.',
  },
  orderSuccess: {
    metaTitle: 'Order confirmed — TCGSecret',
    paidTitle: 'Thank you for your order!',
    paidBody: (email: string) => `We've received your payment. We'll keep you posted at ${email}.`,
    paidBodyNoEmail: "We've received your payment. We'll keep you posted by email.",
    pendingTitle: 'Payment processing',
    pendingBody: "Your payment is still being processed. You'll get an email as soon as it's confirmed.",
    notFoundTitle: 'Order not found',
    notFoundBody: "We couldn't find this order. If you were charged, please contact us and we'll sort it out.",
    reference: 'Order reference',
    preorderNote: 'Your order contains a pre-order, so it ships once the pre-order product is in.',
    backToShop: 'Back to the shop',
  },
  preorders: {
    metaTitle: 'Pre-orders — TCGSecret',
    metaDescription: 'Pre-order upcoming TCG releases at TCGSecret.',
    eyebrow: 'Pre-orders',
    title: 'Pre-order upcoming releases',
    body: 'Secure the next big sets before release day. Pre-orders ship as soon as the stock arrives.',
    emptyTitle: 'No pre-orders open right now',
    emptyBody: 'New releases appear here as soon as pre-orders open.',
  },
  store: {
    eyebrow: 'Coming soon',
    title: 'A TCGSecret store near The Hague',
    body: "We're opening a physical TCGSecret location near The Hague.",
    points: [
      { title: 'Locals', body: 'Local play nights and events will be held at the store.' },
      { title: 'A bigger TCG community', body: 'A place to meet other players and grow the TCG community in the region.' },
    ],
    more: 'More details will follow.',
    announcement: 'Physical store coming soon near The Hague',
  },
};

export type Dictionary = typeof en;

const nl: Dictionary = {
  meta: {
    title: 'TCGSecret — Trading Card Game Shop',
    description: 'Online TCG-winkel. Booster boxes, ETB’s, packs, bundles en meer voor Pokémon, One Piece, Lorcana, Dragon Ball en meer.',
  },
  nav: {
    home: 'Home',
    about: 'Over ons',
    aboutUs: 'Over ons',
    contact: 'Contact',
    categories: 'Categorieën',
    allCategories: 'Alle categorieën',
    allTcgs: "Alle TCG's",
    search: 'Zoeken',
    preorders: 'Pre-orders',
    store: 'Winkel',
  },
  common: {
    soldOut: 'Uitverkocht',
    loading: 'Laden...',
    loadingProduct: 'Product laden...',
    productCount: (n: number) => `${n} product${n === 1 ? '' : 'en'}`,
    noProducts: 'Geen producten gevonden',
    browseAllTcgs: "Bekijk alle TCG's",
  },
  header: {
    searchAria: 'Producten zoeken',
    lightMode: 'Lichte modus',
    darkMode: 'Donkere modus',
    openMenu: 'Menu openen',
    closeMenu: 'Menu sluiten',
    language: 'Taal',
    searchTitle: 'Producten zoeken',
    closeSearch: 'Zoeken sluiten',
    searchPlaceholder: 'Zoek op naam, TCG, set of type…',
    searchButton: 'Zoeken',
    searchHint: <>Typ een zoekterm en klik op <B>Zoeken</B> of druk op <B>Enter</B></>,
    noResultsHint: 'Probeer een andere zoekterm of TCG-filter.',
  },
  results: (count: number, query?: string, game?: string) => (
    <>
      <B>{count}</B> resulta{count !== 1 ? 'ten' : 'at'} gevonden
      {query && <> voor &ldquo;<B>{query}</B>&rdquo;</>}
      {game && <> in <B>{game}</B></>}
    </>
  ),
  hero: {
    eyebrow: 'Trading Card Game Shop',
    tagline: "Jouw kluis voor alles rond trading cards. Booster boxes, elite trainer boxes, packs, bundles en meer — van al je favoriete TCG's.",
    browseAll: "Bekijk alle TCG's",
    aboutShop: 'Over de shop',
    scroll: 'Scroll',
  },
  bento: {
    eyebrow: 'TCG-kluis',
    title: 'Shop per TCG',
    subtitle: "Regelmatig nieuwe TCG's erbij.",
  },
  featured: {
    eyebrow: 'Huidig assortiment',
    title: 'Uitgelichte producten',
    restocking: 'Binnenkort weer op voorraad',
    viewAll: 'Bekijk alle producten',
  },
  about: {
    eyebrow: 'Over TCGSecret',
    title: 'Jouw vertrouwde kluis voor alles rond TCG',
    p1: 'TCGSecret is een online winkel voor liefhebbers en verzamelaars van trading card games. We houden van de spanning van de hobby — van het openen van een verse booster box tot het vinden van die ene kaart die je collectie compleet maakt.',
    p2: "We hebben een breed aanbod aan officiële producten van alle grote TCG's — van de iconische Pokémon TCG tot nieuwkomers als Riftbound en Cyberpunk 2077 TCG.",
    browseProducts: 'Bekijk producten',
  },
  contact: {
    eyebrow: 'Contact',
    title: 'Neem contact op',
    body: 'Vragen over een product, nieuwe voorraad of een bestelling? Stuur ons een e-mail en we reageren zo snel mogelijk.',
  },
  footer: {
    tagline: 'Jouw vertrouwde kluis voor alles rond TCG.',
    about: 'Over ons',
    contact: 'Contact',
    email: 'E-mail',
    phone: 'Telefoon',
    terms: 'Voorwaarden',
    privacy: 'Privacy',
    returns: 'Retourneren',
    shipping: 'Verzending',
    storeSoon: 'Winkel bij Den Haag (binnenkort)',
  },
  category: {
    allProducts: 'Alle producten',
    allSoldOut: 'Alle artikelen zijn momenteel uitverkocht',
    showing: (count: number, category?: string) => (
      <>
        <B>{count}</B> producten
        {category && <> in <B>{category}</B></>}
      </>
    ),
    checkBack: 'Kom snel terug, we vullen onze voorraad aan.',
    exploreOther: "Ontdek andere TCG's",
  },
  product: {
    notFoundTitle: 'Product niet gevonden',
    notFoundBody: 'Het product dat je zoekt bestaat niet of is verwijderd.',
    browseAllProducts: 'Bekijk alle producten',
    restockTitle: 'Nieuwe voorraad onderweg.',
    restockBody: 'We zijn nieuwe voorraad aan het inkopen — kom snel terug of volg ons voor updates.',
    description: 'Beschrijving',
    details: 'Productdetails',
    tcg: 'TCG',
    set: 'Set',
    type: 'Type',
    condition: 'Staat',
    conditionValue: 'Sealed / nieuw',
    region: 'Taal',
    regionValue: 'Engels',
    availability: 'Beschikbaarheid',
    outOfStock: 'Niet op voorraad',
    moreFrom: (game: string) => `Meer ${game}`,
    moreLikeThis: 'Meer zoals dit',
    related: 'Gerelateerde producten',
    viewAll: 'Bekijk alles →',
  },
  search: {
    metaTitle: 'Producten zoeken — TCGSecret',
    metaDescription: 'Zoek TCG-producten bij TCGSecret.',
    clear: 'Zoekopdracht wissen',
    showingAll: (count: number) => <>Alle <B>{count}</B> producten</>,
    noResultsHint: 'Probeer een andere zoekterm of shop per TCG-categorie.',
    clearFilters: 'Filters wissen',
  },
  cookies: {
    title: 'Wij respecteren je privacy',
    body: 'We gebruiken cookies om je surfervaring te verbeteren. Je kiest zelf welke cookies je toestaat. Noodzakelijke cookies zijn altijd actief, omdat de website zonder deze cookies niet werkt.',
    privacyPolicy: 'Privacybeleid',
    necessary: 'Noodzakelijk',
    alwaysActive: '(altijd actief)',
    necessaryDesc: 'Nodig om de website te laten werken. Kan niet worden uitgeschakeld.',
    functional: 'Functioneel',
    functionalDesc: 'Onthoudt je voorkeuren, zoals taal- en regio-instellingen.',
    toggle: { functional: 'Functionele cookies aan/uit' },
    acceptAll: 'Alles accepteren',
    rejectAll: 'Alles weigeren',
    save: 'Voorkeuren opslaan',
    manage: 'Voorkeuren beheren',
  },
  notFound: {
    title: 'Pagina niet gevonden',
    body: 'De pagina die je zoekt bestaat niet. We helpen je terug!',
    back: 'Ga terug',
    home: 'Terug naar home',
  },
  legal: {
    lastUpdated: 'Laatst bijgewerkt',
  },
  shop: {
    addToCart: 'In winkelwagen',
    preorder: 'Pre-order',
    inStock: 'Op voorraad',
    quantity: 'Aantal',
    releases: (date: string) => `Verschijnt ${date}`,
    releaseDate: 'Releasedatum',
    preorderTitle: 'Nu te pre-orderen.',
    preorderBody: 'We versturen zodra de voorraad binnen is. Je kunt kosteloos annuleren zolang de bestelling nog niet is verzonden.',
  },
  cart: {
    title: 'Je winkelwagen',
    open: (n: number) => `Winkelwagen openen (${n} artikel${n === 1 ? '' : 'en'})`,
    close: 'Winkelwagen sluiten',
    empty: 'Je winkelwagen is leeg',
    emptyHint: 'Voeg een booster box toe, of pre-order de volgende grote release.',
    subtotal: 'Subtotaal',
    shippingAtCheckout: 'Verzendkosten worden bij het afrekenen berekend.',
    checkout: 'Afrekenen',
    continueShopping: 'Verder winkelen',
    remove: 'Verwijderen',
    increase: 'Aantal verhogen',
    decrease: 'Aantal verlagen',
    preorderNote: 'Je winkelwagen bevat een pre-order. We versturen alles samen zodra het pre-orderproduct binnen is.',
  },
  checkout: {
    metaTitle: 'Afrekenen — TCGSecret',
    eyebrow: 'Bijna klaar',
    title: 'Afrekenen',
    summary: 'Overzicht van je bestelling',
    shipTo: 'Verzenden naar',
    shipping: 'Verzending',
    free: 'Gratis',
    freeFrom: (amount: string) => `Gratis verzending vanaf ${amount}`,
    delivery: (days: string) => `Bezorgd binnen ${days} werkdagen`,
    total: 'Totaal',
    vatIncluded: 'Incl. btw',
    terms: (
      <>
        Ik ga akkoord met de <Link href="/terms" target="_blank" className="text-primary hover:underline">algemene voorwaarden</Link> en heb het{' '}
        <Link href="/privacy-policy" target="_blank" className="text-primary hover:underline">privacybeleid</Link> gelezen.
      </>
    ),
    pay: 'Bestellen met betaalverplichting',
    redirecting: 'Doorsturen naar betalen…',
    secure: 'Je betaalt veilig op de volgende pagina via onze betaalprovider Stripe. Daar vul je ook je adres in.',
    errorGeneric: 'Er ging iets mis bij het starten van je betaling. Probeer het opnieuw.',
    errorUnavailable: 'Een of meer producten in je winkelwagen zijn niet meer beschikbaar. Controleer je winkelwagen.',
    errorNotConfigured: 'Online betalen is nog niet beschikbaar. Stuur ons een e-mail om je bestelling te plaatsen.',
  },
  orderSuccess: {
    metaTitle: 'Bestelling bevestigd — TCGSecret',
    paidTitle: 'Bedankt voor je bestelling!',
    paidBody: (email: string) => `We hebben je betaling ontvangen. We houden je op de hoogte via ${email}.`,
    paidBodyNoEmail: 'We hebben je betaling ontvangen. We houden je per e-mail op de hoogte.',
    pendingTitle: 'Betaling wordt verwerkt',
    pendingBody: 'Je betaling wordt nog verwerkt. Je ontvangt een e-mail zodra deze is bevestigd.',
    notFoundTitle: 'Bestelling niet gevonden',
    notFoundBody: 'We konden deze bestelling niet vinden. Is er wel geld afgeschreven? Neem dan contact met ons op.',
    reference: 'Bestelreferentie',
    preorderNote: 'Je bestelling bevat een pre-order en wordt verzonden zodra het pre-orderproduct binnen is.',
    backToShop: 'Terug naar de shop',
  },
  preorders: {
    metaTitle: 'Pre-orders — TCGSecret',
    metaDescription: 'Pre-order aankomende TCG-releases bij TCGSecret.',
    eyebrow: 'Pre-orders',
    title: 'Pre-order aankomende releases',
    body: 'Verzeker je van de volgende grote sets vóór de releasedag. Pre-orders versturen we zodra de voorraad binnen is.',
    emptyTitle: 'Er staan momenteel geen pre-orders open',
    emptyBody: 'Nieuwe releases verschijnen hier zodra de pre-orders openen.',
  },
  store: {
    eyebrow: 'Binnenkort',
    title: 'Een TCGSecret-winkel bij Den Haag',
    body: 'We openen binnenkort een fysieke TCGSecret-locatie in de buurt van Den Haag.',
    points: [
      { title: 'Locals', body: 'In de winkel worden lokale speelavonden en events georganiseerd.' },
      { title: 'Een grotere TCG-community', body: 'Een plek om andere spelers te ontmoeten en de TCG-community in de regio te laten groeien.' },
    ],
    more: 'Meer informatie volgt.',
    announcement: 'Fysieke winkel binnenkort bij Den Haag',
  },
};

const DICTIONARIES: Record<Locale, Dictionary> = { en, nl };

export function getDictionary(locale: Locale): Dictionary {
  return DICTIONARIES[locale];
}
